import { Link, useLocation } from "react-router-dom";
import { FolderClosed, Home, ShieldCheck } from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "components/ui/sidebar";
import SidebarProfile from "components/sidebar-profile";
import NotificationCenter from "components/notification-center";
import { useUser } from "context/userContext";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";

export default function AppSidebar() {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const { user } = useUser();
  const { language } = useLanguage();
  const tr = translations[language].sidebarChrome;
  const collapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        {/* The toggle never lives in here — it stays outside, in the content
            pane's own header (see ContentHeader in layout.js), so it's one
            button that tracks the sidebar's edge as it resizes instead of
            jumping between two different spots on collapse. */}
        <Link
          to="/transcribe"
          aria-label={tr.arkiiHomeLabel}
          className="flex items-center gap-2 rounded-md px-1 py-1 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img src="/logo-mark.png" alt="" className="size-6 shrink-0" />
          {!collapsed && (
            <span className="truncate text-lg font-semibold tracking-tight lowercase">
              {translations[language].common.brandName}
            </span>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={pathname.startsWith("/transcribe") || pathname.startsWith("/caption")}
              >
                <Link to="/transcribe">
                  <Home />
                  <span>{tr.navHome}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={pathname.startsWith("/projects") || pathname.startsWith("/__preview/projects")}
              >
                <Link to="/projects">
                  <FolderClosed />
                  <span>{tr.navProjects}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {user?.is_staff === true ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={tr.navAdmin}>
                <a href="https://api.arkii.ai/admin" target="_blank" rel="noopener noreferrer">
                  <ShieldCheck />
                  <span>{tr.navAdmin}</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : null}

        {collapsed ? (
          <div className="flex flex-col items-center gap-1">
            <NotificationCenter />
            <div className="w-full">
              <SidebarProfile />
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-1">
            <div className="min-w-0 flex-1">
              <SidebarProfile />
            </div>
            <NotificationCenter />
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
